import React from "react";
import { CheckCircle2, Circle, Clock } from "lucide-react";
import { formatIndiaDateTime } from "../lib/indiaTime";

const STEPS = [
  { key: "GATE_IN", label: "Gate In" },
  { key: "WEIGH_GROSS", label: "Weigh Gross" },
  { key: "LAB_PENDING", label: "Lab" },
  { key: "BARGAINING", label: "Bargain" },
  { key: "APPROVED", label: "Approved" },
  { key: "UNLOADING", label: "Unload" },
  { key: "WEIGH_TARE", label: "Weigh Tare" },
  { key: "ACCOUNTS_PENDING", label: "Accounts" },
  { key: "COMPLETED", label: "Completed" },
];

const HOLD_STATES = new Set(["UNLOADING_HOLD", "RELAB_PENDING"]);

export default function StateTimeline({ current, history = [] }) {
  const isHold = HOLD_STATES.has(current);
  const effectiveIdx = isHold
    ? STEPS.findIndex((s) => s.key === "UNLOADING")
    : STEPS.findIndex((s) => s.key === current);
  const currentIdx = effectiveIdx;
  const rejected = current === "REJECTED";

  return (
    <div className="w-full">
      <div className="overflow-x-auto pb-2 -mx-1 px-1">
        <div className="flex items-center min-w-[640px]">
        {STEPS.map((s, i) => {
          const done = i < currentIdx || current === "COMPLETED";
          const active = i === currentIdx;
          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center min-w-0">
                <div
                  className={`h-8 w-8 rounded-full grid place-items-center border-2 ${
                    rejected
                      ? "bg-red-50 border-red-400 text-red-600"
                      : isHold && active
                      ? "bg-amber-500 border-amber-500 text-white"
                      : done
                      ? "bg-bansal-gold border-bansal-gold text-white"
                      : active
                      ? "bg-bansal-sage border-bansal-sage text-white"
                      : "bg-card border-border text-gray-400"
                  }`}
                >
                  {done ? <CheckCircle2 className="h-4 w-4" /> : active ? <Clock className="h-4 w-4" /> : <Circle className="h-3 w-3" />}
                </div>
                <div className={`mt-2 text-[10px] uppercase tracking-widest text-center ${active ? (isHold ? "text-amber-600 font-semibold" : "text-primary font-semibold") : "text-muted-foreground"}`}>
                  {isHold && active ? current.replace("_", " ") : s.label}
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-[2px] flex-1 mx-1 ${done ? "bg-bansal-gold" : "bg-muted"}`} />
              )}
            </React.Fragment>
          );
        })}
        </div>
      </div>

      {isHold && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-900">
          {current === "UNLOADING_HOLD"
            ? "Unloading is on hold — supervisor flagged a quality concern. Awaiting manager decision."
            : "Re-lab has been requested by the manager for quality claim negotiation."}
        </div>
      )}

      {rejected && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">
          This purchase was rejected.
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-6 border-t pt-4">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">History</div>
          <div className="space-y-2">
            {history.slice().reverse().map((h, i) => (
              <div key={i} className="text-xs flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-muted-foreground">
                <span className="font-mono shrink-0 sm:w-40 sm:truncate">{formatIndiaDateTime(h.at) || "—"}</span>
                <div className="flex flex-wrap items-center gap-2 min-w-0">
                  <span className="px-2 py-0.5 rounded bg-muted text-foreground font-medium">{h.state}</span>
                  <span className="truncate min-w-0">by {h.by}{h.note ? ` · ${h.note}` : ""}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
